import firebase from "firebase";
import { db } from "../db";

function err(e) {
  return {success: false, error: e};
}

function getRole(userType) {
  if (userType === 2) {
    return "admin";
  } else if (userType === 1) {
    return "developer";
  } else {
    return "tester";
  }
}

function getTimePassed(time) {
  const currentTime = new Date();
  const notificationTime = time.toDate();
  const timePassedMs = currentTime - notificationTime;
  const timePassedMinutes = Math.floor(timePassedMs / (1000 * 60));
  const timePassedHours = Math.floor(timePassedMs / (1000 * 60 * 60));
  const timePassedDays = Math.floor(timePassedMs / (1000 * 60 * 60 * 24));

  if (timePassedHours < 1) {
    if (timePassedMinutes === 1) {
      return `${timePassedMinutes} minute ago`;
    } else {
      return `${timePassedMinutes} minutes ago`;
    }
  } else if (timePassedDays < 1) {
    if (timePassedHours === 1) {
      return `${timePassedHours} hour ago`;
    } else {
      return `${timePassedHours} hours ago`;
    }
  } else if (timePassedDays < 13) {
    if (timePassedDays === 1) {
      return `${timePassedDays} day ago`;
    } else {
      return `${timePassedDays} days ago`;
    }
  } else {
    return `${notificationTime.getMonth() + 1}/${notificationTime.getDate()}/${notificationTime.getFullYear()}`;
  }
}

async function createUserNotification(userId, type, title, content = "") {
  return new Promise((resolve, reject) => {
    db.collection("Notification").doc().set({
      userId,
      type,
      title,
      content,
      read: false,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    })
    .then(() => resolve({success: true}))
    .catch(e => reject(err(e)));
  });
}

async function createRoleNotification(userType, type, title, content = "", meta = {}) {
  return new Promise((resolve, reject) => {
    db.collection("User").where("userType", "==", getRole(userType)).get()
      .then(querySnapshot => {
        const batch = db.batch();
        querySnapshot.forEach(doc => {
          const newDoc = db.collection("Notification").doc();
          batch.set(newDoc, {
            userId: doc.id,
            type,
            title,
            content,
            read: false,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            ...meta,
          })
        });
        batch.commit()
          .then(() => resolve({success: true}))
          .catch(e => reject(err(e)));
      })
      .catch(e => reject(err(e)));
  });
}

async function getUserNotifications(userId) {
  return new Promise((resolve, reject) => {
    db.collection("Notification").where("userId", "==", userId).orderBy("createdAt", "desc").get()
      .then(querySnapshot => {
        let notifications = [];
        querySnapshot.forEach(doc => {
          let newDoc = {
            ...doc.data(),
            id: doc.id,
            timePassed: getTimePassed(doc.data().createdAt),
          };
          notifications.push(newDoc);
        });
        resolve({success: true, data: notifications});
      })
      .catch(e => reject(err(e)));
  });
}

function watchUserNotifications(userId, callback) {
  const unsubscribe = db.collection("Notification")
    .where("userId", "==", userId)
    .orderBy("createdAt", "desc")
    .onSnapshot(querySnapshot => {
      let notifications = [];
      querySnapshot.forEach(doc => {
        let newDoc = {
          ...doc.data(),
          id: doc.id,
          timePassed: getTimePassed(doc.data().createdAt),
        };
        notifications.push(newDoc);
      });
      callback({success: true, data: notifications});
    }, e => callback(err(e)));
  return unsubscribe;
}

function watchNewUserNotifications(userId, lastFetch, callback) {
  const unsubscribe = db.collection("Notification")
    .where("userId", "==", userId)
    .where("createdAt", ">", lastFetch)
    .orderBy("createdAt", "desc")
    .onSnapshot(querySnapshot => {
      let notifications = [];
      let newLastFetch;
      querySnapshot.docChanges().forEach(change => {
        if (change.type === "added") {
          let newDoc = {
            id: change.doc.id,
            userId: change.doc.data().userId,
            type: change.doc.data().type,
            title: change.doc.data().title,
            content: change.doc.data().content,
            read: change.doc.data().read,
            createdAt: change.doc.data().createdAt,
            timePassed: getTimePassed(change.doc.data().createdAt),
          };
          notifications.push(newDoc);

          if (!newLastFetch) {
            newLastFetch = change.doc.data().createdAt;
          }
        }
      });
      callback({success: true, data: notifications, lastFetch: newLastFetch});
    }, e => callback(err(e)));
  return unsubscribe;
}

async function deleteUserNotifications(userId, timeFrame) {
  return new Promise((resolve, reject) => {
    let timeThreshold;
    if (timeFrame === "All") {
      timeThreshold = 0;
    } else if (timeFrame === "Older than 1 Hour") {
      timeThreshold = 60 * 60;
    } else if (timeFrame === "Older than 1 Day") {
      timeThreshold = 24 * 60 * 60;
    } else {
      timeThreshold = 7 * 24 * 60 * 60;
    }

    const now = firebase.firestore.Timestamp.now();
    const timeStamp = new firebase.firestore.Timestamp(now.seconds - timeThreshold, now.nanoseconds);
    db.collection("Notification").where("userId", "==", userId).where("createdAt", "<", timeStamp).get()
      .then(querySnapshot => {
        const batch = db.batch();
        querySnapshot.forEach(doc => {
          batch.delete(doc.ref);
        });
        batch.commit()
          .then(() => resolve({success: true}))
          .catch(e => reject(err(e)));
      })
      .catch(e => reject(err(e)));
  });
}

async function deleteNotificationsBySurvey(surveyId) {
  return new Promise((resolve, reject) => {
    if (!surveyId) {
      resolve({success: true, deleted: 0});
      return;
    }
    db.collection("Notification").where("surveyId", "==", surveyId).get()
      .then(querySnapshot => {
        const batch = db.batch();
        querySnapshot.forEach(doc => {
          batch.delete(doc.ref);
        });
        batch.commit()
          .then(() => resolve({success: true, deleted: querySnapshot.size}))
          .catch(e => reject(err(e)));
      })
      .catch(e => reject(err(e)));
  });
}

async function markNotificationRead(notificationId) {
  return new Promise((resolve, reject) => {
    db.collection("Notification").doc(notificationId).update({ read: true })
      .then(() => resolve({success: true}))
      .catch(e => reject(err(e)));
  });
}

async function deleteNotificationById(notificationId) {
  return new Promise((resolve, reject) => {
    db.collection("Notification").doc(notificationId).delete()
      .then(() => resolve({success: true}))
      .catch(e => reject(err(e)));
  });
}

export default {
  createUserNotification,
  createRoleNotification,
  getUserNotifications,
  watchUserNotifications,
  watchNewUserNotifications,
  deleteUserNotifications,
  deleteNotificationsBySurvey,
  deleteNotificationById,
  markNotificationRead,
};



// WEBPACK FOOTER //
// ./src/Firestore/Notifications.js